import { forComponent } from './logging';

const log = forComponent('jobs');

/**
 * Job registry — ONE tracker for every async unit of tool work the harness may have to reason about:
 * a sandbox `bash({background:true})`, a background sub-agent, a real OS shell job (`ShellJobRegistry`
 * composes this), an MCP call handed off past its soft timeout, a delegated runtime's host/native tool.
 * Edge-safe (no node imports): OS specifics (spawn, CPU probes) are injected by the producer.
 *
 * Each job carries LIVENESS, not just status: `lastActivityAt` is bumped by output chunks, MCP progress
 * notifications, native tool events, or an injected CPU probe. That is what lets the harness tell a slow
 * job from a stuck one — a job is only reaped when it has been silent past its kind's idle window (and a
 * CPU probe, when one exists, says it is not burning CPU either), or when it passed its hard cap. A reaped
 * job gets `status:'killed'` + a `reason` (`stuck: idle 600s`) the model sees; the run itself continues.
 *
 * IMPORTANT (sandbox kinds): concurrency via async-I/O interleaving, NOT parallelism — a CPU-bound JS
 * producer still blocks the thread. Overlay isolation / commit-on-success stay the PRODUCER's job (IoC).
 */
export type JobStatus = 'running' | 'done' | 'error' | 'killed';
/** Well-known kinds (free-form strings are allowed for host producers). */
export type JobKind = 'shell' | 'mcp' | 'host' | 'native' | 'bash' | 'task' | (string & {});

export interface JobCtx {
  signal: AbortSignal;
  /** Append output to the job's tail buffer (also counts as activity). */
  onChunk: (s: string) => void;
  /** Record liveness without output (e.g. an MCP progress notification). */
  touch: (note?: string) => void;
}
/** A producer: does the work, may stream via `onChunk`/`touch`, resolves its result. */
export interface JobFn { (ctx: JobCtx): Promise<unknown> }

export interface JobTrackOptions {
  kind?: JobKind;
  label?: string;
  /** Output produced before the registry took over (e.g. a foreground shell's output at handoff). */
  seed?: string;
  /** No activity for this long ⇒ stuck (0 = never idle-reaped). Default: `JobRegistryOptions.idleMs[kind]`. */
  idleMs?: number;
  /** Absolute wall-clock ceiling (0 = none). Default: `JobRegistryOptions.hardCapMs[kind]`. */
  hardCapMs?: number;
  /** Cumulative CPU seconds of the job's process(es), or undefined when unknown. Advancing CPU counts as activity. */
  cpuTime?: () => Promise<number | undefined>;
  /** How to actually stop the work (abort a request, SIGTERM a group). Called once, on kill. */
  onKill?: (reason: string) => void;
  /** Await this job in `drain()` (turn end). Default false for tracked jobs, true for `start()`. */
  drain?: boolean;
  /** Report the terminal state to `onExit` (the model). Default false; `detach()` turns it on. */
  notify?: boolean;
  /** Per-job terminal-state handler, used INSTEAD of the registry-wide `onExit` (a producer with its own notice format). */
  onExit?: (n: JobNotice) => void;
  /** Something is synchronously waiting on this job (a delegated runtime's stream) — it holds the model stall watchdog. */
  holds?: boolean;
  /** Arm the idle window only once the job has shown activity (`chunk`/`touch`). For producers whose silence proves
   *  nothing until they demonstrated a liveness channel — an MCP server that never sends progress is just working. */
  idleAfterActivity?: boolean;
}

export interface JobInfo {
  id: string; kind: JobKind; label: string; status: JobStatus;
  startedAt: number; lastActivityAt: number; endedAt?: number;
  /** Why it ended when that was not its own doing (`stuck: idle 600s`, `killed by JobKill`). */
  reason?: string;
  bytes: number; holds: boolean;
}

export interface JobNotice { id: string; kind: JobKind; label: string; status: JobStatus; reason?: string; tail: string; result?: unknown }

export class JobRegistryOptions {
  /** Tail buffer cap per job (chars). */
  maxBuffer = 256 * 1024;
  /** Idle window per kind: no output/progress/CPU for this long ⇒ stuck. Kinds without an activity channel
   *  (host, native) are absent on purpose — silence is all they can ever show, so it proves nothing; their
   *  bound is the hard cap. Explicit `background:true` shell jobs opt out per job (a quiet server is healthy). */
  idleMs: Record<string, number> = { shell: 10 * 60_000, mcp: 10 * 60_000 };
  /** Absolute ceiling per kind (0/absent = none). */
  hardCapMs: Record<string, number> = { mcp: 30 * 60_000 };
  /** Soft foreground timeout per kind: past it the caller stops waiting and gets a job id instead. */
  foregroundMs: Record<string, number> = { mcp: 30_000 };
  /** How often the reaper runs while reapable jobs exist. */
  sweepMs = 15_000;
  /** Grace between the kill signal and a forced kill (producers that escalate read this). */
  killGraceMs = 5_000;
  /** Finished jobs kept for inspection; older finished ones are pruned (every delegated tool call is a job). */
  keepFinished = 200;
  /** Clock (injectable for tests). Late-bound so a patched Date.now is honoured. */
  now: () => number = () => Date.now();
  /** Terminal-state notification for jobs with `notify` (the Agent wires this to `inject`). */
  onExit?: (n: JobNotice) => void;
}

interface Job extends JobInfo {
  buf: string; dropped: number; result?: unknown;
  idleMs: number; hardCapMs: number; drain: boolean; notify: boolean; idleAfterActivity: boolean; active: boolean;
  cpuTime?: () => Promise<number | undefined>; lastCpu?: number;
  onKill?: (reason: string) => void; onExit?: (n: JobNotice) => void;
  done: Promise<void>; settle: () => void;
}

/** Handle to a tracked job: feed it activity, then settle it exactly once. */
export interface JobHandle {
  id: string;
  chunk(s: string): void;
  touch(note?: string): void;
  finish(status: Exclude<JobStatus, 'running'>, result?: unknown, reason?: string): void;
  /** Stop foreground-waiting: from now on the terminal state is reported via `onExit` and awaited by `drain()`. */
  detach(): void;
  readonly status: JobStatus;
}

const secs = (ms: number) => `${Math.round(ms / 1000)}s`;

export class JobRegistry {
  public options: JobRegistryOptions;
  private jobs = new Map<string, Job>();
  private seq = 0;
  private timer?: ReturnType<typeof setInterval>;

  constructor(options?: Partial<JobRegistryOptions>) {
    this.options = { ...new JobRegistryOptions(), ...options };
  }

  /** Register externally-driven work (a process, a request, a native tool) and get a handle to feed it. */
  track(opts: JobTrackOptions = {}): JobHandle {
    const o = this.options;
    const kind = opts.kind ?? 'task';
    const now = o.now();
    let settle!: () => void;
    const done = new Promise<void>((r) => { settle = r; });
    const job: Job = {
      id: `job-${++this.seq}`, kind, label: opts.label ?? '', status: 'running', startedAt: now, lastActivityAt: now,
      bytes: 0, holds: !!opts.holds, buf: '', dropped: 0,
      idleMs: opts.idleMs ?? o.idleMs[kind] ?? 0, hardCapMs: opts.hardCapMs ?? o.hardCapMs[kind] ?? 0,
      drain: !!opts.drain, notify: !!opts.notify, idleAfterActivity: !!opts.idleAfterActivity, active: false, cpuTime: opts.cpuTime, onKill: opts.onKill, onExit: opts.onExit, done, settle,
    };
    this.jobs.set(job.id, job);
    if (opts.seed) this.append(job, opts.seed);
    this.arm();
    return {
      id: job.id,
      chunk: (s) => { if (s) { this.append(job, s); job.lastActivityAt = o.now(); job.active = true; } },
      touch: () => { job.lastActivityAt = o.now(); job.active = true; },
      finish: (status, result, reason) => this.end(job, status, result, reason),
      detach: () => { job.holds = false; job.notify = true; job.drain = true; },
      get status() { return job.status; },
    };
  }

  /** Start `fn` in the background and return a job id immediately (sandbox/subagent semantics: drained at turn end). */
  start(fn: JobFn, opts: JobTrackOptions = {}): string {
    return this.run(fn, { drain: true, ...opts }).handle.id;
  }

  /**
   * Run `fn` in the FOREGROUND with a soft timeout: resolves `{done:true,value}` if it settles in time, else
   * `{done:false,id}` — the work keeps going as a detached job whose completion is reported via `onExit`.
   * A rejection before the timeout is rethrown (the caller's normal error path). `softTimeoutMs` ≤ 0 waits forever.
   */
  async runForeground<T>(fn: (ctx: JobCtx) => Promise<T>, opts: JobTrackOptions & { softTimeoutMs?: number } = {}):
    Promise<{ done: true; value: T } | { done: false; id: string }> {
    const { handle, promise } = this.run(fn, opts);
    const soft = opts.softTimeoutMs ?? this.options.foregroundMs[opts.kind ?? 'task'] ?? 0;
    let t: ReturnType<typeof setTimeout> | undefined;
    const late = soft > 0 ? new Promise<'late'>((r) => { t = setTimeout(() => r('late'), soft); }) : new Promise<never>(() => {});
    try {
      const r = await Promise.race([promise.then((v) => ({ v }), (e) => ({ e })), late]);
      // Settled inline: the caller has the value and the id was never exposed — don't retain its output/result.
      if (r !== 'late' && handle.status !== 'running') this.jobs.delete(handle.id);
      if (r === 'late') {
        if (handle.status === 'running') { handle.detach(); return { done: false, id: handle.id }; }
        const settled = await promise.then((v) => ({ v }), (e) => ({ e })); // settled in the same tick as the timer
        if ('e' in settled) throw settled.e;
        return { done: true, value: settled.v as T };
      }
      if ('e' in r) throw r.e;
      return { done: true, value: r.v as T };
    } finally { if (t) clearTimeout(t); }
  }

  private run<T>(fn: (ctx: JobCtx) => Promise<T>, opts: JobTrackOptions): { handle: JobHandle; promise: Promise<T> } {
    const ctl = new AbortController();
    const handle = this.track({ ...opts, onKill: (reason) => { ctl.abort(Object.assign(new Error(reason), { name: 'AbortError', code: 'job_killed' })); opts.onKill?.(reason); } });
    let promise: Promise<T>;
    try { promise = fn({ signal: ctl.signal, onChunk: handle.chunk, touch: handle.touch }); } // sync start: output is visible immediately
    catch (e) { promise = Promise.reject(e); }
    promise.then(
      (res) => { if (handle.status === 'running' && typeof res === 'string' && res) handle.chunk(res); handle.finish('done', res); },
      (e: any) => { if (handle.status === 'running') handle.chunk(`\n[error] ${e?.message ?? e}`); handle.finish('error', undefined, e?.message ?? String(e)); },
    );
    return { handle, promise };
  }

  private append(job: Job, s: string): void {
    job.buf += s;
    const over = job.buf.length - this.options.maxBuffer;
    if (over > 0) { job.buf = job.buf.slice(over); job.dropped += over; } // ring: keep the tail
    job.bytes = job.buf.length;
  }

  private end(job: Job, status: Exclude<JobStatus, 'running'>, result?: unknown, reason?: string): void {
    if (job.status !== 'running') return; // first terminal state wins (a kill beats the late settle)
    job.status = status; job.endedAt = this.options.now(); job.result = result;
    if (reason && status !== 'done') job.reason = reason;
    job.holds = false;
    job.settle();
    this.prune();
    const onExit = job.onExit ?? this.options.onExit;
    if (job.notify && onExit) {
      try { onExit({ id: job.id, kind: job.kind, label: job.label, status, reason: job.reason, tail: job.buf.slice(-4000), result }); }
      catch (e) { log.warn(`job onExit handler threw for ${job.id}: ${e}`); } // a host notification must never take the registry down
    }
  }

  private prune(): void {
    const finished = [...this.jobs.values()].filter((j) => j.status !== 'running');
    for (const j of finished.slice(0, Math.max(0, finished.length - this.options.keepFinished))) this.jobs.delete(j.id);
  }

  /** Current tail output (null = no such job). */
  output(id: string): string | null { return this.jobs.get(id)?.buf ?? null; }

  /** Incremental read: output from absolute char `offset` (clamped to what the ring still holds). */
  read(id: string, offset = 0): { text: string; next: number; truncated: boolean } | null {
    const j = this.jobs.get(id);
    if (!j) return null;
    const start = Math.max(0, offset - j.dropped);
    return { text: j.buf.slice(start), next: j.dropped + j.buf.length, truncated: offset < j.dropped };
  }

  get(id: string): (JobInfo & { result?: unknown }) | null {
    const j = this.jobs.get(id);
    return j ? this.info(j) : null;
  }

  status(id: string): { status: JobStatus; bytes: number; reason?: string } | null {
    const j = this.jobs.get(id);
    return j ? { status: j.status, bytes: j.bytes, ...(j.reason ? { reason: j.reason } : {}) } : null;
  }

  list(): JobInfo[] { return [...this.jobs.values()].map((j) => this.info(j)); }

  private info(j: Job): JobInfo & { result?: unknown } {
    const { id, kind, label, status, startedAt, lastActivityAt, endedAt, reason, bytes, holds, result } = j;
    return { id, kind, label, status, startedAt, lastActivityAt, endedAt, reason, bytes, holds, result };
  }

  /** Wait (bounded) for a job to settle. Resolves the job's info either way; null for an unknown id. */
  async wait(id: string, timeoutMs: number): Promise<JobInfo | null> {
    const j = this.jobs.get(id);
    if (!j) return null;
    let t: ReturnType<typeof setTimeout> | undefined;
    await Promise.race([j.done, new Promise<void>((r) => { t = setTimeout(r, Math.max(0, timeoutMs)); })]);
    if (t) clearTimeout(t);
    return this.info(j);
  }

  /** Stop a job: runs its `onKill` (abort/SIGTERM) and marks it killed with `reason`. */
  kill(id: string, reason = 'killed by request'): boolean {
    const j = this.jobs.get(id);
    if (!j) return false;
    if (j.status === 'running') {
      this.end(j, 'killed', undefined, reason); // terminal FIRST: a producer that exits synchronously on the signal must not land as 'done'
      try { j.onKill?.(reason); } catch (e) { log.warn(`onKill for ${id} threw: ${e}`); }
    }
    return true;
  }

  /** Kill every running job — or, with `onlyDrainable`, only turn-scoped ones (sandbox/sub-agent/handed-off MCP),
   *  leaving jobs meant to outlive a run (a background dev server) alone. */
  killAll(reason = 'run ended', onlyDrainable = false): void {
    for (const j of this.jobs.values()) if (!onlyDrainable || j.drain) this.kill(j.id, reason);
  }

  /** Jobs something is synchronously waiting on (see `JobTrackOptions.holds`) that are still running. */
  holding(): JobInfo[] { return [...this.jobs.values()].filter((j) => j.status === 'running' && j.holds).map((j) => this.info(j)); }

  /** Await every drainable running job (turn end). Returns the ids that were still running when drain began. */
  async drain(signal?: AbortSignal): Promise<string[]> {
    const pending = [...this.jobs.values()].filter((j) => j.status === 'running' && j.drain);
    const all = Promise.all(pending.map((j) => j.done));
    // A user cancel must not hang on a slow handed-off job (it keeps running; the next turn can still collect it).
    await (signal ? Promise.race([all, new Promise<void>((r) => (signal.aborted ? r() : signal.addEventListener('abort', () => r(), { once: true })))]) : all);
    return pending.map((j) => j.id);
  }

  /**
   * The stuck-job decision, synchronous part: kill every running job past its hard cap, or past its idle
   * window when it has no CPU probe to argue otherwise. Cheap — the stall watchdog calls it on every tick.
   * Returns the killed ids. Jobs WITH a probe are left to `sweep()`.
   */
  reap(): string[] {
    const now = this.options.now();
    const killed: string[] = [];
    for (const j of this.jobs.values()) {
      if (j.status !== 'running') continue;
      const verdict = this.verdict(j, now);
      if (!verdict || (verdict.idle && j.cpuTime)) continue;
      this.stuck(j, verdict.reason, now); killed.push(j.id);
    }
    return killed;
  }

  /** Full sweep: sample CPU probes (advancing CPU = activity), then `reap()` including probed jobs. */
  async sweep(): Promise<string[]> {
    const now = this.options.now();
    for (const j of [...this.jobs.values()]) {
      if (j.status !== 'running' || !j.cpuTime || !j.idleMs) continue;
      let cpu: number | undefined;
      try { cpu = await j.cpuTime(); } catch (e) { log.debug(`cpu probe for ${j.id} failed: ${e}`); }
      if (cpu != null && j.lastCpu != null && cpu > j.lastCpu) j.lastActivityAt = this.options.now(); // still burning CPU → alive
      const prev = j.lastCpu;
      if (cpu != null) j.lastCpu = cpu;
      const verdict = this.verdict(j, now);
      if (!verdict?.idle || j.status !== 'running') continue;
      // No CPU evidence (probe unavailable / failed) is not proof of death, and the first sample is only a baseline.
      if (cpu == null) { log.debug(`not reaping ${j.id}: ${verdict.reason} but CPU unknown`); continue; }
      if (prev == null) continue;
      this.stuck(j, `${verdict.reason}, no CPU progress (${prev}s → ${cpu}s)`, now);
    }
    return this.reap();
  }

  private verdict(j: Job, now: number): { reason: string; idle: boolean } | null {
    if (j.hardCapMs && now - j.startedAt >= j.hardCapMs) return { reason: `stuck: exceeded hard cap ${secs(j.hardCapMs)}`, idle: false };
    if (j.idleMs && (!j.idleAfterActivity || j.active) && now - j.lastActivityAt >= j.idleMs) return { reason: `stuck: idle ${secs(now - j.lastActivityAt)}`, idle: true };
    return null;
  }

  private stuck(j: Job, reason: string, now: number): void {
    // Every kill decision is logged with its evidence — a reaped job must be explainable after the fact.
    log.warn(`reaping ${j.id} (${j.kind} "${j.label}"): ${reason} — age ${secs(now - j.startedAt)}, last activity ${secs(now - j.lastActivityAt)} ago, `
      + `idleMs=${j.idleMs}, hardCapMs=${j.hardCapMs}, ${j.bytes} byte(s) output`);
    // Something was actually stopped → the model must learn why its job vanished, even if it was never detached.
    // A pure holder (host/native: no kill handle) is only released; the stream it held will speak for itself.
    if (j.onKill) j.notify = true;
    this.kill(j.id, reason);
  }

  /** Keep a reaper interval alive only while a reapable job is running (unref'd: never holds the process open). */
  private arm(): void {
    if (this.timer || !this.options.sweepMs) return;
    this.timer = setInterval(() => {
      const live = [...this.jobs.values()].some((j) => j.status === 'running' && (j.idleMs || j.hardCapMs));
      if (!live) { clearInterval(this.timer); this.timer = undefined; return; }
      void this.sweep().catch((e) => log.warn(`job sweep failed: ${e}`));
    }, this.options.sweepMs);
    (this.timer as any).unref?.();
  }

  /** Stop the reaper timer (tests / teardown). */
  dispose(): void { if (this.timer) clearInterval(this.timer); this.timer = undefined; }
}

/** Back-compat name: the sandbox registry IS the generic registry. */
export class SandboxJobRegistry extends JobRegistry {
  constructor(maxBuffer?: number | Partial<JobRegistryOptions>) {
    super(typeof maxBuffer === 'number' ? { maxBuffer } : maxBuffer);
  }
}

/** The text a job's terminal-state notice becomes when pushed to the model. */
export function formatJobNotice(n: JobNotice): string {
  return `[job ${n.id} ${n.status}${n.reason ? `: ${n.reason}` : ''}] ${n.kind}${n.label ? ` \`${n.label}\`` : ''}\n`
    + (n.tail ? `${n.tail.replace(/\n+$/, '')}\n` : '(no output)\n')
    + `Read the full output with JobOutput({id:"${n.id}"}).`;
}

const NO_JOB = (id: string) => `Error: no background job '${id}'. Use JobStatus with no id to list jobs.`;
const describe = (j: JobInfo, now: number) =>
  `${j.id}  ${j.status}${j.reason ? ` (${j.reason})` : ''}  ${j.kind}${j.label ? '  ' + j.label : ''}  `
  + `· ${secs((j.endedAt ?? now) - j.startedAt)} old · last activity ${secs(now - j.lastActivityAt)} ago · ${j.bytes} byte(s)`;

/** Model-facing tools over a registry: JobOutput (tail / incremental), JobStatus (one / list), JobKill, JobWait (bounded). */
export function makeJobTools(registry: JobRegistry, opts: { maxWaitMs?: number } = {}): import('./tools').AgentTool[] {
  const maxWaitMs = opts.maxWaitMs ?? 120_000;
  return [
    {
      name: 'JobOutput',
      description: 'Read a job\'s output by id. Without `offset`: the buffered tail. With `offset` (the `next` value from a previous read): only what arrived since.',
      parameters: { type: 'object', required: ['id'], properties: { id: { type: 'string' }, offset: { type: 'number', description: 'return output after this position (from a previous read\'s `next`)' } } },
      async run({ id, offset }) {
        const r = registry.read(String(id), Number(offset) || 0);
        if (!r) return NO_JOB(String(id));
        const st = registry.status(String(id))!;
        return `[${st.status}${st.reason ? `: ${st.reason}` : ''}] next=${r.next}${r.truncated ? ' (older output dropped)' : ''}\n${r.text.replace(/\n+$/, '') || '(no new output)'}`;
      },
    },
    {
      name: 'JobStatus',
      description: 'Status of a job (running/done/error/killed, age, last activity). Omit `id` to list all jobs.',
      parameters: { type: 'object', properties: { id: { type: 'string' } } },
      async run({ id }) {
        const now = registry.options.now();
        if (!id) { const all = registry.list(); return all.length ? all.map((j) => describe(j, now)).join('\n') : '(no background jobs)'; }
        const j = registry.get(String(id));
        return j ? describe(j, now) : NO_JOB(String(id));
      },
    },
    {
      name: 'JobKill',
      description: 'Stop a running job by id (sandbox jobs are cancelled without committing; processes get SIGTERM; MCP calls are cancelled).',
      parameters: { type: 'object', required: ['id'], properties: { id: { type: 'string' } } },
      async run({ id }) {
        return registry.kill(String(id), 'killed by JobKill') ? `Killed job ${id}.` : NO_JOB(String(id));
      },
    },
    {
      name: 'JobWait',
      description: `Block until a job finishes or \`timeoutMs\` passes (max ${maxWaitMs}ms), then return its status and output tail. Prefer this over sleeping.`,
      parameters: { type: 'object', required: ['id'], properties: { id: { type: 'string' }, timeoutMs: { type: 'number' } } },
      async run({ id, timeoutMs }) {
        const j = await registry.wait(String(id), Math.min(Number(timeoutMs) || maxWaitMs, maxWaitMs));
        if (!j) return NO_JOB(String(id));
        const tail = (registry.output(j.id) ?? '').slice(-4000).replace(/\n+$/, '');
        return `${describe(j, registry.options.now())}\n${tail || '(no output)'}`;
      },
    },
  ];
}
